import api from "./apiService";
import type {
  CreateCheckoutSessionResponse,
  PaymentStatusResponse,
  Plan,
} from "../types/payments";

// Map each plan to its amount in cents and default currency
const planAmounts: Record<Plan, number> = {
  free: 0,           // Free plan - no charge
  basic: 5000,       // $50/month
  pro: 7500,         // $75/month
  enterprise: 0,     // Contact sales - no direct checkout
};

/**
 * Create a Payment Intent for a given plan.
 * Backend expects { plan, context } and returns { sessionId: string, url: string }.
 */
export const createCheckoutSession = async (
  plan: Plan,
  context?: string
): Promise<CreateCheckoutSessionResponse> => {
  const { data } = await api.post<CreateCheckoutSessionResponse>(
    "/payments/create-checkout-session",
    { plan, context }
  );
  return data;
};

/**
 * Fetch the status of a payment intent by its ID.
 * Backend expects a query param `paymentIntentId` and returns { status: string }.
 * We attach the original ID to our response shape.
 */
export const getPaymentStatus = async (
  sessionId: string
): Promise<PaymentStatusResponse> => {
  const response = await api.get<{ status: string }>("/payments/status", {
    params: { sessionId },
  });

  const { status } = response.data;
  return { sessionId, status };
};

/**
 * Create a Stripe Customer Portal session for subscription management.
 * Returns the portal URL where users can cancel, change plan, update payment method, and view invoices.
 */
export const createBillingPortalSession = async (): Promise<{ url: string }> => {
  const { data } = await api.post<{ url: string }>("/payments/billing-portal");
  return data;
};

// --- Promo Code Types ---

export interface PromoValidationResult {
  valid: boolean;
  plan_tier?: string;
  duration_months?: number;
  error?: string;
}

export interface PromoRedemptionResult {
  success: boolean;
  redemption?: { plan_tier: string; expires_at: string };
  error?: string;
}

export interface PromoStatusResult {
  active: boolean;
  plan_tier?: string;
  expires_at?: string;
  days_remaining?: number;
}

export interface PromoCode {
  id: string;
  code: string;
  description: string | null;
  plan_tier: string;
  duration_months: number;
  max_redemptions: number | null;
  current_redemptions: number;
  valid_from: string;
  valid_until: string | null;
  is_active: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreatePromoCodeData {
  code: string;
  description?: string;
  plan_tier?: string;
  duration_months?: number;
  max_redemptions?: number | null;
  valid_until?: string | null;
}

// --- Promo Code API Functions ---

export const validatePromoCode = async (
  code: string
): Promise<PromoValidationResult> => {
  const { data } = await api.post<PromoValidationResult>("/api/promo/validate", { code });
  return data;
};

export const redeemPromoCode = async (
  code: string
): Promise<PromoRedemptionResult> => {
  const { data } = await api.post<PromoRedemptionResult>("/api/promo/redeem", { code });
  return data;
};

export const getPromoStatus = async (): Promise<PromoStatusResult> => {
  const { data } = await api.get<PromoStatusResult>("/api/promo/status");
  return data;
};

// --- Admin Promo Code API Functions ---

export const createAdminPromoCode = async (
  promoData: CreatePromoCodeData
): Promise<{ success: boolean; data?: PromoCode; error?: string }> => {
  const { data } = await api.post<{ success: boolean; data?: PromoCode; error?: string }>(
    "/api/admin/promo",
    promoData
  );
  return data;
};

export const listAdminPromoCodes = async (): Promise<{
  success: boolean;
  data: PromoCode[];
}> => {
  const { data } = await api.get<{ success: boolean; data: PromoCode[] }>("/api/admin/promo");
  return data;
};

export const updateAdminPromoCode = async (
  id: string,
  updateData: Partial<Pick<PromoCode, "is_active" | "max_redemptions" | "description" | "valid_until">>
): Promise<{ success: boolean; data?: PromoCode; error?: string }> => {
  const { data } = await api.patch<{ success: boolean; data?: PromoCode; error?: string }>(
    `/api/admin/promo/${id}`,
    updateData
  );
  return data;
};

/**
 * Initialize the user's plan in usage_tracking.
 * Call this after free plan selection or as a fallback after paid checkout
 * to ensure the user's plan is immediately available for feature gating.
 */
export const initializePlan = async (
  plan: Plan
): Promise<{ success: boolean; plan_tier?: string; error?: string }> => {
  try {
    const response = await api.post<{
      success: boolean;
      data?: { plan_tier: string; message: string };
      error?: string;
    }>("/api/usage/initialize-plan", { plan });

    if (response.data.success) {
      return {
        success: true,
        plan_tier: response.data.data?.plan_tier,
      };
    } else {
      return {
        success: false,
        error: response.data.error || "Failed to initialize plan",
      };
    }
  } catch (error: any) {
    console.error("Error initializing plan:", error);
    return {
      success: false,
      error: error?.response?.data?.error || error?.message || "Failed to initialize plan",
    };
  }
};
